import { type VariantProps } from 'class-variance-authority'
import { useSpinDelay } from 'spin-delay'
import { cn } from '#app/utils/misc.tsx'
import { Button, type buttonVariants } from './button.tsx'
import { Icon } from './icon.tsx'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from './tooltip.tsx'

type Status = 'pending' | 'success' | 'error' | 'idle'

type ButtonBaseProps = Omit<
	React.ComponentProps<'button'>,
	keyof VariantProps<typeof buttonVariants>
>

interface StatusButtonProps
	extends ButtonBaseProps,
		VariantProps<typeof buttonVariants> {
	status: Status
	message?: string | null
	spinDelay?: Parameters<typeof useSpinDelay>[1]
}

export function StatusButton({
	message,
	status,
	className,
	children,
	spinDelay,
	...props
}: StatusButtonProps) {
	const delayedPending = useSpinDelay(status === 'pending', {
		delay: 400,
		minDuration: 300,
		...spinDelay,
	})

	const companion = {
		pending: delayedPending ? (
			<div
				role="status"
				className="inline-flex size-6 items-center justify-center"
			>
				<Icon name="update" className="animate-spin" title="loading" />
			</div>
		) : null,
		success: (
			<div
				role="status"
				className="inline-flex size-6 items-center justify-center"
			>
				<Icon name="check" title="success" />
			</div>
		),
		error: (
			<div
				role="status"
				className="bg-destructive inline-flex size-6 items-center justify-center rounded-full"
			>
				<Icon
					name="cross-1"
					className="text-destructive-foreground"
					title="error"
				/>
			</div>
		),
		idle: null,
	}[status] as React.ReactNode

	return (
		<Button className={cn('flex justify-center gap-4', className)} {...props}>
			<div>{children}</div>
			{message ? (
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger>{companion}</TooltipTrigger>
						<TooltipContent>{message}</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			) : (
				companion
			)}
		</Button>
	)
}

StatusButton.displayName = 'StatusButton'
